/**
 * Persistent OAuth session and static model selection for one subscription provider.
 * @module dsh-coding-subscription-oauth/oauth-session
 */

import { mkdir, readFile, rm } from "node:fs/promises";
import { dirname, join, resolve } from "node:path";
import { resolveDshHome } from "@deepseek-ai/dsh-home-paths";
import type {
	Api,
	AuthInteraction,
	Credential,
	CredentialStore,
	Model,
	MutableModels,
	OAuthCredential,
	Provider,
} from "@earendil-works/pi-ai";
import { createModels } from "@earendil-works/pi-ai";
import { ModelCacheQueue, writeModelCache } from "./model-cache.js";
import type { OAuthProviderDefinition } from "./oauth-providers.js";
import { currentPoolAccountOverride } from "./quota-pool.js";
import { type LoginPersistOptions, OAuthCredentialFileStore, oauthCredentialPath } from "./store.js";

const MODELS_CACHE_VERSION = 2;

interface ModelsCacheDocument {
	version: typeof MODELS_CACHE_VERSION;
	selectionMode: "default" | "selected";
	selected: string[];
}

function isENOENT(error: unknown): boolean {
	return (error as NodeJS.ErrnoException | null)?.code === "ENOENT";
}

function parseIdList(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	return [...new Set(value.filter((id): id is string => typeof id === "string" && id.length > 0))];
}

function parseCache(text: string): string[] | undefined {
	let value: unknown;
	try {
		value = JSON.parse(text);
	} catch {
		return undefined;
	}
	if (typeof value !== "object" || value === null || Array.isArray(value)) return undefined;
	const document = value as Record<string, unknown>;
	if (document["version"] !== 1 && document["version"] !== MODELS_CACHE_VERSION) return undefined;
	const selected = parseIdList(document.selected);
	if (document["version"] === MODELS_CACHE_VERSION)
		return document["selectionMode"] === "selected" ? selected : undefined;
	return selected.length === 0 ? undefined : selected;
}

export function oauthModelsCachePath(basename: string, dshHome?: string): string {
	return resolve(join(resolveDshHome(dshHome), basename));
}

export interface OAuthProviderStatus {
	authenticated: boolean;
	expiresAt?: number;
}

export class OAuthProviderSession {
	readonly store: OAuthCredentialFileStore;
	readonly models: MutableModels;
	private readonly catalog: readonly Model<Api>[];
	private readonly cacheFile: string;
	private selectedIds: string[] | undefined;
	private readonly cacheQueue = new ModelCacheQueue();
	private readonly onCatalogChange: (() => void) | undefined;
	private readonly onCredentialChange: (() => void) | undefined;

	constructor(
		readonly definition: OAuthProviderDefinition,
		onCatalogChange?: () => void,
		store: OAuthCredentialFileStore = new OAuthCredentialFileStore(
			definition.nativeProviderId,
			oauthCredentialPath(definition.authFilename),
			definition.route,
		),
		cacheFile: string = oauthModelsCachePath(definition.modelsCacheFilename),
		onCredentialChange?: () => void,
		/** Optional CredentialStore overlay (pool proxy); defaults to `store`. */
		credentials?: CredentialStore,
	) {
		this.store = store;
		this.cacheFile = resolve(cacheFile);
		this.onCatalogChange = onCatalogChange;
		this.onCredentialChange = onCredentialChange;
		const provider = definition.providerFactory();
		this.catalog = [...provider.getModels()];
		this.models = createModels({ credentials: credentials ?? store });
		this.models.setProvider(provider);
	}

	availableModels(): Model<Api>[] {
		return [...this.catalog];
	}

	selectedModelIds(): string[] | undefined {
		return this.selectedIds === undefined ? undefined : [...this.selectedIds];
	}

	visibleModels(): Model<Api>[] {
		if (this.selectedIds === undefined) return this.availableModels();
		const byId = new Map(this.catalog.map((model) => [model.id, model]));
		return this.selectedIds.flatMap((id) => {
			const model = byId.get(id);
			return model === undefined ? [] : [model];
		});
	}

	provider(): Provider {
		return this.definition.requestProvider(this.visibleModels().map((model) => model.id));
	}

	async loadCachedModels(): Promise<void> {
		try {
			this.selectedIds = parseCache(await readFile(this.cacheFile, "utf8"));
		} catch (error) {
			if (!isENOENT(error)) throw error;
		}
	}

	async setSelectedModels(ids: readonly string[] | undefined): Promise<void> {
		const selected = ids === undefined ? undefined : [...new Set(ids.filter((id) => id.length > 0))];
		await this.cacheQueue.run(async () => {
			await this.writeCache(selected);
			this.selectedIds = selected;
			this.onCatalogChange?.();
		});
	}

	async status(): Promise<OAuthProviderStatus> {
		const credential = await this.store.read(this.definition.nativeProviderId);
		if (credential?.type !== "oauth") return { authenticated: false };
		return { authenticated: true, expiresAt: credential.expires };
	}

	async login(interaction: AuthInteraction, persist: LoginPersistOptions = { mode: "add" }): Promise<Credential> {
		const credential = await this.store.runLoginPersist(persist, () =>
			this.models.login(this.definition.nativeProviderId, "oauth", interaction),
		);
		this.onCatalogChange?.();
		this.onCredentialChange?.();
		return credential;
	}

	notifyCredentialChange(): void {
		this.onCredentialChange?.();
	}

	async resolveAccessToken(): Promise<string | undefined> {
		const resolved = await this.models.getAuth(this.definition.nativeProviderId);
		// Prefer the resolved auth surface so a pool override (request-scoped
		// account) is not replaced by a subsequent active-account file read.
		return resolved?.auth.apiKey;
	}

	/**
	 * Backdate the stored token's expiry so the next `getAuth()` refreshes.
	 * Called after an upstream 401 rejected a locally-valid token.
	 */
	async invalidateAccessToken(): Promise<void> {
		const override = currentPoolAccountOverride();
		if (override?.providerId === this.definition.nativeProviderId) {
			await this.store.modifyAccount(override.accountId, async (current) => {
				if (current?.type !== "oauth") return undefined;
				return { ...current, expires: Date.now() - 1000 };
			});
			return;
		}
		await this.store.invalidate(this.definition.nativeProviderId);
	}

	async storedCredential(): Promise<OAuthCredential | undefined> {
		const credential = await this.store.read(this.definition.nativeProviderId);
		return credential?.type === "oauth" ? credential : undefined;
	}

	async logout(): Promise<void> {
		return this.cacheQueue.run(async () => {
			try {
				await this.models.logout(this.definition.nativeProviderId);
				this.selectedIds = undefined;
				await mkdir(dirname(this.cacheFile), { recursive: true, mode: 0o700 });
				await rm(this.cacheFile, { force: true });
			} finally {
				// Credential deletion may succeed before cache cleanup fails. Always
				// refresh discovery so an open selector cannot retain stale models.
				this.onCatalogChange?.();
				this.onCredentialChange?.();
			}
		});
	}

	private async writeCache(selected: string[] | undefined): Promise<void> {
		const document: ModelsCacheDocument = {
			version: MODELS_CACHE_VERSION,
			selected: selected === undefined ? [] : [...selected],
			selectionMode: selected === undefined ? "default" : "selected",
		};
		await writeModelCache(this.cacheFile, document);
	}
}
